import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";
import type { ProxySummary } from "@/lib/proxies";
import { Label } from "@/components/ui/label";

export type ProxyMode = "by_country" | "any" | "specific";

interface Props {
  mode: ProxyMode;
  onMode: (m: ProxyMode) => void;
  proxyId: string;
  onProxyId: (id: string) => void;
  proxies: ProxySummary[];
  /** When false, by_country mode falls back to "any" since we have no phone
   * to derive country from. We still let the operator pick it as intent. */
  byCountrySupported: boolean;
  /** Operator-confirmed override for the shared-proxy fleet warning. Set
   * by the parent when the user checks the "I understand the risk" box
   * below the yellow banner; passed to the import endpoint as
   * `confirm_shared_proxy=true` so the backend's 400 guard lets the
   * request through. */
  confirmShared?: boolean;
  onConfirmShared?: (v: boolean) => void;
  /** How many accounts this import brings. Two or more onto one fixed proxy
   * is the same fleet pattern as a proxy that is already in use — the backend
   * refuses both without the confirmation. */
  incomingCount?: number;
}

/**
 * Single source of truth for proxy selection in import flows.
 *
 * Three mutually-exclusive modes:
 * 1. by_country (default) — random from pool, prefer same-country as the
 *    session's phone. Falls back to any-country if no match.
 * 2. any — pure random from pool.
 * 3. specific — operator pins one proxy for the whole batch (rarely useful
 *    for bulk; kept for parity with single-import).
 */
export function ProxyChoice({
  mode,
  onMode,
  proxyId,
  onProxyId,
  proxies,
  byCountrySupported,
  confirmShared,
  onConfirmShared,
  incomingCount = 1,
}: Props) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <Label>{t("accounts.proxy_choice.title")}</Label>
      <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
        <button
          type="button"
          onClick={() => onMode("by_country")}
          className={`rounded-lg border px-3 py-2 transition text-left ${
            mode === "by_country"
              ? "border-accent bg-accent/15 text-accent"
              : "border-border text-fg-muted hover:text-fg-primary"
          }`}
          title={
            byCountrySupported
              ? undefined
              : (t("accounts.proxy_choice.by_country_unsupported_hint") ?? "")
          }
        >
          <div className="font-medium">
            {t("accounts.proxy_choice.by_country")}
          </div>
          <div className="text-fg-muted text-xs mt-0.5">
            {t("accounts.proxy_choice.by_country_hint")}
          </div>
        </button>
        <button
          type="button"
          onClick={() => onMode("any")}
          className={`rounded-lg border px-3 py-2 transition text-left ${
            mode === "any"
              ? "border-accent bg-accent/15 text-accent"
              : "border-border text-fg-muted hover:text-fg-primary"
          }`}
        >
          <div className="font-medium">{t("accounts.proxy_choice.any")}</div>
          <div className="text-fg-muted text-xs mt-0.5">
            {t("accounts.proxy_choice.any_hint")}
          </div>
        </button>
        <button
          type="button"
          onClick={() => onMode("specific")}
          className={`rounded-lg border px-3 py-2 transition text-left ${
            mode === "specific"
              ? "border-accent bg-accent/15 text-accent"
              : "border-border text-fg-muted hover:text-fg-primary"
          }`}
        >
          <div className="font-medium">
            {t("accounts.proxy_choice.specific")}
          </div>
          <div className="text-fg-muted text-xs mt-0.5">
            {t("accounts.proxy_choice.specific_hint")}
          </div>
        </button>
      </div>
      {mode === "specific" && (
        <>
          <select
            value={proxyId}
            onChange={(e) => onProxyId(e.target.value)}
            className="w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm"
            required
          >
            <option value="">{t("accounts.field.proxy_none")}</option>
            {proxies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
                {p.country_code ? ` · ${p.country_code}` : ""}
                {p.assigned_accounts_count > 0
                  ? ` · ${t("accounts.proxy_choice.already_used", { count: p.assigned_accounts_count })}`
                  : ""}
                {p.is_alive === false ? ` · ${t("accounts.proxy_choice.dead")}` : ""}
              </option>
            ))}
          </select>
          {(() => {
            const picked = proxies.find((p) => p.id === proxyId);
            if (!picked) return null;
            const inUse = picked.assigned_accounts_count > 0;
            if (!inUse && incomingCount <= 1) return null;
            // Hard-warn: live-test 2026-05-08 — 7 of 8 accounts on the
            // same shared proxy got `FrozenMethodInvalid` from TG within
            // 24-48h. Each fresh import on an already-used proxy
            // contributes to TG's fleet-pattern signal.
            return (
              <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm space-y-2">
                <div className="flex items-center gap-1.5 font-medium text-warning">
                  <TriangleAlert size={16} aria-hidden="true" className="shrink-0" />
                  {inUse
                    ? t("accounts.proxy_choice.shared_warn_title", {
                        count: picked.assigned_accounts_count,
                        label: picked.label,
                      })
                    : t("accounts.proxy_choice.shared_warn_title_batch", {
                        count: incomingCount,
                        label: picked.label,
                      })}
                </div>
                <div className="text-xs text-fg-secondary">
                  {t("accounts.proxy_choice.shared_warn_body")}
                </div>
                {onConfirmShared && (
                  <label className="flex items-start gap-2 text-xs text-fg-secondary cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!confirmShared}
                      onChange={(e) => onConfirmShared(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      {t("accounts.proxy_choice.shared_confirm_checkbox")}
                    </span>
                  </label>
                )}
              </div>
            );
          })()}
        </>
      )}
      {mode === "by_country" && !byCountrySupported && (
        <p className="text-xs text-warning">
          {t("accounts.proxy_choice.by_country_unsupported_hint")}
        </p>
      )}
      {(mode === "by_country" || mode === "any") && proxies.length === 0 && (
        <p className="text-xs text-warning">
          {t("accounts.proxy_choice.empty_pool")}
        </p>
      )}
    </div>
  );
}
